<template>
  <v-dialog
    :value="visible"
    @input="$emit('close')"
    max-width="900"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title> Mapping — {{ network ? network.name : "" }} </v-card-title>

      <v-card-text style="max-height: 65vh" v-if="is_loading_mapping">
        <div>Loading...</div></v-card-text
      >
      <v-card-text v-else style="max-height: 65vh">
        <v-btn small color="primary" class="mb-3" @click="addParameter">
          <v-icon left small>mdi-plus</v-icon> Add Parameter
        </v-btn>

        <div
          v-for="(param, idx) in parameters"
          :key="idx"
          class="parameter-row mb-4 pa-3 elevation-1"
        >
          <v-row dense>
            <v-col cols="3">
              <v-text-field
                v-model="param.name"
                placeholder="e.g. phone, firstName"
                label="Parameter Name"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col>
              <!-- placeholder="e.g. {first_name}+{last_name}" -->
              <v-text-field
                v-model="param.template"
                label="Template *"
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="auto">
              <v-checkbox
                v-model="param.optional"
                label="Optional"
                dense
                hide-details
                class="mt-1"
              />
            </v-col>
            <v-col cols="auto d-flex align-center">
              <div class="d-flex">
                <v-btn icon small color="red" @click="removeParameter(idx)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <!-- Field Transforms -->
          <div
            v-if="param.showTransforms || param.transforms.length"
            class="mt-3 ml-4 pl-3 transform-section"
          >
            <div class="text-caption mb-2">Field Transforms:</div>
            <v-row
              v-for="(tf, tIdx) in param.transforms"
              :key="tIdx"
              dense
              class="mb-2"
            >
              <v-col cols="4">
                <v-select
                  :items="fields.filter((f) => f.name == 'phone')"
                  label="Field Name"
                  v-model="tf.field"
                  item-text="label"
                  item-value="name"
                  dense
                  outlined
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="tf.transform"
                  :items="transformOptions"
                  label="Transform"
                  dense
                  outlined
                  hide-details
                />
              </v-col>
              <v-col cols="auto">
                <v-btn
                  icon
                  x-small
                  color="red"
                  @click="param.transforms.splice(tIdx, 1)"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-btn
              x-small
              text
              color="primary"
              @click="
                param.transforms.push({ field: '', transform: 'remove_plus' })
              "
            >
              + Add Transform
            </v-btn>
          </div>
          <v-btn
            v-else
            x-small
            text
            color="grey"
            class="mt-2"
            @click="param.showTransforms = true"
          >
            + Add Field Transforms
          </v-btn>
        </div>

        <div v-if="!parameters.length" class="text-center grey--text pa-6">
          No parameters configured. Click "Add Parameter" to start.
        </div>
      </v-card-text>

      <div class="available-fields px-4 pb-3">
        <v-btn
          text
          small
          color="info"
          size="small"
          @click="showAvailableFields = !showAvailableFields"
        >
          <v-icon left small>mdi-lightbulb-outline</v-icon>
          {{ showAvailableFields ? "Hide" : "Show" }} Available Parameter Fields
        </v-btn>

        <v-expand-transition>
          <div v-show="showAvailableFields" class="mt-2">
            <v-card outlined>
              <v-card-text class="pa-3">
                <v-chip
                  v-for="field in fields"
                  :key="field.name"
                  small
                  class="ma-1"
                  outlined
                >
                  {{ field.name }}
                </v-chip>
              </v-card-text>
            </v-card>
          </div>
        </v-expand-transition>
      </div>

      <v-card-actions>
        <v-btn
          v-if="mappingId"
          color="error"
          text
          @click="deleteMapping"
          :loading="deleteLoading"
        >
          Delete Mapping
        </v-btn>
        <v-spacer />
        <v-btn text @click="$emit('close')">Cancel</v-btn>
        <v-btn color="primary" :loading="saving" @click="save">
          {{ mappingId ? "Update" : "Create" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  createMapping,
  updateMapping,
  getMapping,
  deleteMapping as deleteMappingApi,
} from "@/store/modules/advertisers/api";
import { requestStatus } from "@/services/services";

export default {
  name: "MappingDialog",
  props: {
    visible: Boolean,
    network: Object,
    mappingId: [String, Number],
  },
  data: () => ({
    is_loading_mapping: false,
    parameters: [],
    saving: false,
    deleteLoading: false,
    showAvailableFields: false,
    transformOptions: [
      { text: "remove_plus", value: "remove_plus" },
      { text: "ensure_plus", value: "ensure_plus" },
    ],
    fields: [
      { name: "first_name", label: "First Name" },
      { name: "last_name", label: "Last Name" },
      { name: "email", label: "Email" },
      { name: "phone", label: "Phone" },
      { name: "city", label: "City" },
      { name: "zip", label: "Zip Code" },
      { name: "address", label: "Address" },
      { name: "state", label: "State" },
      { name: "Lang", label: "Language" },
      { name: "title", label: "Title" },
    ],
  }),
  watch: {
    visible(open) {
      if (open) {
        this.loadMapping();
      }
    },
  },
  methods: {
    loadMapping() {
      if (this.mappingId) {
        this.is_loading_mapping = true;
        getMapping(this.mappingId)
          .then((response) => {
            if (response.status === requestStatus.success) {
              const mappingData = response.response.data.mapping || {};
              this.parameters = Object.entries(mappingData).map(
                ([name, config]) => ({
                  name,
                  template: config.template || "",
                  optional: config.optional || false,
                  transforms: Object.entries(config.field_transforms || {}).map(
                    ([field, transform]) => ({
                      field,
                      transform,
                    }),
                  ),
                  showTransforms:
                    Object.keys(config.field_transforms || {}).length > 0,
                }),
              );
            } else {
              this.$toaster.error(
                response.messages || "Failed to load mapping",
              );
              this.parameters = [];
              this.$emit("close");
            }
          })
          .finally(() => (this.is_loading_mapping = false));
      } else {
        this.parameters = [];
      }
    },

    addParameter() {
      this.parameters.push({
        name: "",
        template: "",
        optional: false,
        transforms: [],
        showTransforms: false,
      });
    },

    removeParameter(idx) {
      this.parameters.splice(idx, 1);
    },

    buildMappingObject() {
      const mapping = {};
      for (const param of this.parameters) {
        if (!param.name || !param.template) continue;

        const entry = { template: param.template };

        const validTransforms = param.transforms.filter(
          (t) => t.field && t.transform,
        );
        if (validTransforms.length) {
          entry.field_transforms = {};
          validTransforms.forEach((t) => {
            entry.field_transforms[t.field] = t.transform;
          });
        }

        if (param.optional) {
          entry.optional = true;
        }

        mapping[param.name] = entry;
      }
      return mapping;
    },

    save() {
      const mapping = this.buildMappingObject();

      if (Object.keys(mapping).length === 0) {
        this.$toaster.error("Please add at least one parameter");
        return;
      }

      this.saving = true;
      const payload = {
        network: this.network.id,
        mapping,
      };

      const apiCall = this.mappingId
        ? updateMapping(this.mappingId, { mapping })
        : createMapping(payload);

      apiCall
        .then((response) => {
          if (response.status === requestStatus.success) {
            this.$toaster.success(
              this.mappingId
                ? "Mapping updated successfully"
                : "Mapping created successfully",
            );
            this.$emit("saved");
          } else {
            this.$toaster.error(response.messages || "Operation failed");
          }
        })
        .finally(() => (this.saving = false));
    },

    deleteMapping() {
      if (!this.mappingId) return;

      window.getApp.$refs.global_confirm_modal
        .open("Confirm remove mapping")
        .then((confirm) => {
          if (confirm) {
            this.deleteLoading = true;
            deleteMappingApi(this.mappingId)
              .then((response) => {
                if (response.status === requestStatus.success) {
                  this.$toaster.success("Mapping deleted successfully");
                  this.$emit("saved");
                } else {
                  this.$toaster.error(
                    response.messages || "Failed to delete mapping",
                  );
                }
              })
              .finally(() => (this.deleteLoading = false));
          }
        });
    },
  },
};
</script>

<style scoped>
.parameter-row {
  border-left: 4px solid #1976d2;
  background: #fafafa;
}

.transform-section {
  border-left: 3px solid #ff9800;
  background: #fff3e0;
}
</style>
